import { Box, Grid, Typography, useTheme } from "@mui/material"
import { styled } from "@mui/material/styles"
import { Container } from "@mui/system"
import { useApiGet } from "auth/useApi2"
import { useApi } from "auth/useApi3"
import ContinueDialog from "components/Dialogs/ContinueDialog"
import { GlobalLoaderContext } from "components/GlobalLoadingProvider"
import PaperBox from "components/PaperBox"
import { TodoListWrapper } from "components/dataDisplay/TodoList/TodoListWrapper"
import { Formik } from "formik"
import { apiEndpoints } from "generated/apiEndpoints"
import { Fragment, useContext, useEffect, useState } from "react"
import * as Yup from "yup"
import ExternalDetailsCard from "./components/ExternalDetailsCard"
import ExternalHeader from "./components/ExternalHeader"
import ExternalIssueReports from "./components/ExternalIssueReports"
import ExternalReadMessageCard from "./components/ExternalReadMessageCard"
import ExternalSendButton from "./components/ExternalSendButton"
import ExternalStatusBox from "./components/ExternalStatusBox/ExternalStatusBox"
import { useTranslation } from "react-i18next"

const ExternalValidationSchema = (t) =>
  Yup.object().shape({
    plannedDate: Yup.date().required(t("global.date_is_required", "Date is required")).nullable()
  })

const StyledPaperBox = styled(PaperBox)(({ theme }) => ({
  boxShadow: "0px 0px 15px 0px rgb(0 0 0 / 4%)"
}))

const Workorder = ({ match }) => {
  const [workorderResult, workorderApi] = useApi()
  const [logoResult, getLogoData] = useApiGet()
  const loadingContext = useContext(GlobalLoaderContext)
  const [sendBookingResult, sendBookingApi] = useApi()
  const [sendReportResult, sendReportApi] = useApi()
  const [cancelReportResult, cancelReportApi] = useApi()
  const [showUpdateModal, setShowUpdateModal] = useState(false)
  const [showSendReportModal, setShowSendReportModal] = useState(false)
  const { t } = useTranslation()
  const theme = useTheme()

  // fetch data and filter
  useEffect(() => {
    workorderApi.get(apiEndpoints.issuereportexternal.getworkorderbyguid.replace("{guid}", match.params.guid))
  }, [workorderApi, match])

  // update workorder
  const updateStatus = (values) => {
    if (values.status === 2) {
      sendBookingApi.put(apiEndpoints.issuereportexternal.sendworkorderbooking, values)
    }
    if (values.status === 3) {
      cancelReportApi.put(apiEndpoints.issuereportexternal.declineworkorder, values)
    }
  }

  // send report
  const sendReport = (values) => {
    setShowSendReportModal(false)
    sendReportApi.put(apiEndpoints.issuereportexternal.sendworkorderresolved, values)
  }

  // handle submit formik form & show continue modal
  const handleSubmit = () => {
    setShowUpdateModal(true)
  }

  // handle submit formik form & show continue modal
  const handleSendReport = () => {
    setShowSendReportModal(true)
  }

  // fetch data and filter
  useEffect(() => {
    if (cancelReportResult.status === 2) {
      workorderApi.get(
        apiEndpoints.issuereportexternal.getworkorderbyguid.replace("{guid}", match.params.guid)
      )
      setShowUpdateModal(false)
      return () => {
        workorderApi.leave()
      }
    }
  }, [workorderApi, match, cancelReportResult])

  // fetch data and filter
  useEffect(() => {
    if (sendReportResult.status === 2) {
      workorderApi.get(
        apiEndpoints.issuereportexternal.getworkorderbyguid.replace("{guid}", match.params.guid)
      )
      setShowUpdateModal(false)
      return () => {
        workorderApi.leave()
      }
    }
  }, [workorderApi, match, sendReportResult])

  // fetch data and filter
  useEffect(() => {
    if (sendBookingResult.status === 2) {
      workorderApi.get(
        apiEndpoints.issuereportexternal.getworkorderbyguid.replace("{guid}", match.params.guid)
      )
      setShowUpdateModal(false)
      return () => {
        workorderApi.leave()
      }
    }
  }, [workorderApi, match, sendBookingResult])

  useEffect(() => {
    loadingContext.setLoading(() => workorderResult.status === 1)
  })

  useEffect(() => {
    if (workorderResult.status === 2 && workorderResult.data.customerLogo) {
      getLogoData(
        apiEndpoints.issuereportexternal.getcustomerlogobyname.concat(
          "?name=" + workorderResult.data.customerLogo
        )
      )
    }
  }, [workorderResult, getLogoData])

  const [initialValues, setInitialValues] = useState({})

  useEffect(() => {
    if (workorderResult.status === 2) {
      setInitialValues(workorderResult.data)
    }
  }, [workorderResult])

  return (
    <Container>
      {workorderResult && workorderResult.status === 2 && (
        <Fragment>
          <ExternalHeader t={t} workorderResult={workorderResult} logoResult={logoResult} />
          <Formik
            validationSchema={ExternalValidationSchema(t)}
            initialValues={initialValues}
            onSubmit={handleSubmit}
            enableReinitialize={true}>
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              isSubmitting,
              handleSubmit,
              setFieldValue
            }) => (
              <form id="WorkOrderForm" onSubmit={handleSubmit} noValidate>
                <Grid mt={2} pb={3} container spacing={2} justifyContent="space-between">
                  <Grid item xs={12} md={8}>
                    <PaperBox m={0} p={5}>
                      <ExternalReadMessageCard
                        bgColor={theme.palette.info.light}
                        title={t("global.message", "Message")}
                        message={values.message}
                      />
                      <ExternalDetailsCard
                        t={t}
                        title={t("global.vehicle_data", "Vehicle data")}
                        values={workorderResult.data}
                        bgColor={theme.palette.info.light}
                      />

                      {values.status === 2 && (
                        <Box mt={5}>
                          <Typography fontWeight={500} mb={1} variant="h5">
                            {t("issuereports.list_of_issuereports", "List of issue reports")}
                          </Typography>
                          <StyledPaperBox sx={{ p: 0 }}>
                            <TodoListWrapper>
                              {values.issueReports.map((issueReport, index) => (
                                <ExternalIssueReports
                                  handleChange={handleChange}
                                  setFieldValue={setFieldValue}
                                  values={values}
                                  issueReport={issueReport}
                                  index={index}
                                />
                              ))}
                            </TodoListWrapper>
                          </StyledPaperBox>
                        </Box>
                      )}
                    </PaperBox>

                    {values.status > 2 && (
                      <Box mt={5}>
                        <Typography fontWeight={400} variant="h1">
                          {t("global.work_report", "Work report")}
                        </Typography>
                        <StyledPaperBox sx={{ p: values.status > 2 ? 4 : 0 }}>
                          <Box>
                            {values.status > 2 && (
                              <Typography fontWeight={500} mb={1} variant="h5">
                                {t("issuereports.list_of_issuereports", "List of issue reports")}
                              </Typography>
                            )}
                            {values.status === 3 && (
                              <Typography mb={2} variant="bodyMedium">
                                {t(
                                  "issuereports.mark_the_issureport_that_has_been_fixed",
                                  "Mark the issue reports that have been fixed"
                                )}
                              </Typography>
                            )}
                          </Box>
                          <TodoListWrapper>
                            {values.issueReports.map((issueReport, index) => (
                              <ExternalIssueReports
                                setFieldValue={setFieldValue}
                                values={values}
                                issueReport={issueReport}
                                index={index}
                              />
                            ))}
                          </TodoListWrapper>
                          {values.status === 3 && (
                            <ExternalSendButton
                              t={t}
                              onAddSubmit={handleSendReport}
                              sendReport={sendReport}
                              buttonFeedbackResult={sendReportResult}
                            />
                          )}
                        </StyledPaperBox>
                      </Box>
                    )}
                  </Grid>
                  <ExternalStatusBox
                    t={t}
                    status={values?.status ? values?.status : 2}
                    values={values}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    touched={touched}
                    buttonFeedbackResult={values.status < 3 ? sendBookingResult : cancelReportResult}
                    errors={errors}
                    setFieldValue={setFieldValue}
                  />
                </Grid>

                {showUpdateModal && (
                  <ContinueDialog
                    result={values.status < 3 ? sendBookingResult : cancelReportResult}
                    onAddSubmit={updateStatus}
                    values={values}
                    setShowContinueModal={setShowUpdateModal}
                    cancelBtnText={t("global.cancel", "Cancel")}
                    continueBtnText={
                      values.status < 3 ? t("global.send", "Send") : t("global.cancel", "Cancel")
                    }
                    message={
                      values.status < 3
                        ? t(
                            "issuereports.do_you_want_to_submit_a_date",
                            "Do you want to submit a date reservation for a work order?"
                          )
                        : t(
                            "issuereports.do_you_want_to_cancel_a_workorder",
                            "Do you want to cancel a work order?"
                          )
                    }
                  />
                )}

                {showSendReportModal && (
                  <ContinueDialog
                    result={sendReportResult}
                    onAddSubmit={sendReport}
                    values={values}
                    setShowContinueModal={setShowSendReportModal}
                    cancelBtnText={t("global.buttons.cancel", "Cancel")}
                    continueBtnText={t("global.send", "Send")}
                    message={t(
                      "issuereports.do_you_want_to_submit_a_workorder",
                      "Do you want to submit work order?"
                    )}
                  />
                )}
              </form>
            )}
          </Formik>
        </Fragment>
      )}
    </Container>
  )
}

export default Workorder
